import { staleSyncJobs } from '@budget/core';
import { useExpiringIntegrations, useLatestSyncRuns } from './use-sync-status.js';
import type { SyncStatus } from '../../types/db.js';

const STATUS_LABEL: Record<SyncStatus, string> = {
  running: 'Fut',
  success: 'Rendben',
  partial: 'Részleges',
  error: 'Hiba',
};

export function SyncStatusPanel() {
  const runs = useLatestSyncRuns();
  const expiring = useExpiringIntegrations();

  // A job that fails says so in its own row. A job that stopped being called
  // says nothing at all — the last good run sits there, green, indefinitely.
  const stale = staleSyncJobs(runs.data ?? []);

  return (
    <section className="card" aria-labelledby="sync-status-heading">
      <h2 id="sync-status-heading" className="card__title">
        Sync állapot
      </h2>

      {expiring.data && expiring.data.length > 0 ? (
        <ul className="alert-list" data-testid="expiry-warnings">
          {expiring.data.map((integration) => (
            <li key={integration.id} className="alert alert--warning">
              <strong>{integration.provider}</strong>{' '}
              {integration.daysLeft < 0
                ? `hozzáférése lejárt ${Math.abs(integration.daysLeft)} napja — újra kell kötni.`
                : `hozzáférése ${integration.daysLeft} nap múlva lejár.`}
            </li>
          ))}
        </ul>
      ) : null}

      {stale.length > 0 ? (
        <ul className="alert-list" data-testid="stale-warnings">
          {stale.map((entry) => (
            <li key={entry.job} className="alert alert--warning">
              <strong>{entry.job}</strong> {entry.daysAgo} napja nem futott. Az ütemezés áll — nézd
              meg a GitHub Actions <em>Sync</em> workflow-ját.
            </li>
          ))}
        </ul>
      ) : null}

      {runs.isPending ? <p className="muted">Betöltés…</p> : null}
      {runs.isError ? (
        <p className="alert alert--error">Nem sikerült lekérni a sync futásokat.</p>
      ) : null}

      {runs.data && runs.data.length === 0 ? (
        <p className="muted">Még nem futott egyetlen sync sem. A 2. fázisban indul.</p>
      ) : null}

      {runs.data && runs.data.length > 0 ? (
        <table className="table" data-testid="sync-runs">
          <thead>
            <tr>
              <th scope="col">Job</th>
              <th scope="col">Állapot</th>
              <th scope="col">Rekordok</th>
              <th scope="col">Utolsó futás</th>
            </tr>
          </thead>
          <tbody>
            {runs.data.map((run) => (
              <tr key={run.id}>
                <td>{run.job}</td>
                <td>
                  <span className={`badge badge--${run.status}`}>{STATUS_LABEL[run.status]}</span>
                </td>
                <td className="numeric">{run.records}</td>
                <td>
                  <time dateTime={run.started_at}>
                    {new Date(run.started_at).toLocaleString('hu-HU')}
                  </time>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}
